import { APP_ID, isDevMode, NgModule, signal } from '@angular/core';
import { AgrBrowserModule } from '@next/core-lib/browser';

import { parseAgrEnvironment } from '@next/core-lib/environment';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';

import { AppComponent } from './app.component';
import { AppModule } from './app.module';
declare global {
  var CONFIG_BUILD_ID: string | undefined;
}
const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';
    const response = await fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parseAgrEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

export const preserveAgrSessionSnapshot = (): void => {
  try {
    const search = typeof globalThis === 'undefined' ? '' : (globalThis.location?.search ?? '');
    if (!search) return;
    const urlParams = new URLSearchParams(search);
    let agrSession = urlParams.get('AGR_SESSION');
    // If not found, try parsing encoded parameter names
    if (!agrSession) {
      for (const [key] of urlParams.entries()) {
        if (key.includes('AGR_SESSION')) {
          const parts = decodeURIComponent(key).split('=');
          if (parts.length === 2 && parts[0] === 'AGR_SESSION') {
            agrSession = parts[1];
            break;
          }
        }
      }
    }
    if (agrSession) {
      globalThis.sessionStorage?.setItem('AGR_SESSION_SNAPSHOT', agrSession);
    }
  } catch (e) {
    console.warn('Failed to preserve AGR_SESSION snapshot', e);
  }
};

@NgModule({
  imports: [AppModule, AgrBrowserModule],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => {
        return loadedConfig();
      },
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    provideStore(),
    provideEffects(),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions when extension window is not open
      trace: false, // Stack trace for actions
      traceLimit: 75, // Maximum stack trace frames
      connectInZone: false, // Connect outside Angular zone to prevent unnecessary change detection
    }),
  ],
})
export class AppBrowserModule {}
